import { estLivraisonBloquee } from './actions.js';
/**
 * Motifs de livraison effectuée, par langue.
 *
 * Pris sur les libellés réellement rendus par les transporteurs que Mosaik
 * utilise, et volontairement larges : un transporteur écrit « Livré »,
 * « LIVRAISON EFFECTUEE » ou « Livré au client » sans prévenir. La comparaison
 * se fait sans casse ni accents (cf. `aplatir`), donc `livre` attrape aussi
 * « LIVRÉ ».
 */
const MOTIFS_LIVRE = [
    // Français — « livré », « livraison effectuée », « remis au destinataire ».
    /\blivr(e|ee|es|ees)\b/,
    /livraison\s+(effectuee|realisee|terminee)/,
    /remis\s+au\s+destinataire/,
    // Anglais — « delivered », « we have delivered », « proof of delivery ».
    /\bdelivered\b/,
    /\bpod\b/,
    // Allemand — « zugestellt », « Sendung zugestellt ».
    /\bzugestellt\b/,
    /\bausgeliefert\b/,
    // Italien — « consegnato », « consegna effettuata ».
    /\bconsegnat[oa]\b/,
    /consegna\s+effettuata/,
    // Espagnol — « entregado », « entrega realizada ».
    /\bentregad[oa]\b/,
    /entrega\s+realizada/,
];
/**
 * Ce qui ressemble à une livraison sans en être une, SANS être un blocage.
 *
 * Testé avant `MOTIFS_LIVRE`, parce que « en cours de livraison » contient
 * « livraison ». Sans cette garde, un colis en tournée serait classé arrivé — et
 * la commande passerait en archive alors que le client attend toujours.
 *
 * **Cette liste s'est vidée, et c'est le correctif.** Elle comptait douze
 * motifs, dont huit — « non livré », « not delivered », « undelivered »,
 * « échec de livraison », « nicht zugestellt »… — figuraient déjà dans
 * `MOTIFS_LIVRAISON_BLOQUEE`, testée juste avant. Ces huit-là étaient donc
 * INATTEIGNABLES : deux listes à tenir pour un seul effet, et une incohérence
 * qui se voyait à l'écran — « non livré » levait une alerte rouge, « pas
 * livré » passait pour un transit ordinaire. Un échec de livraison est un
 * blocage ; il se déclare à un seul endroit, celui où la liste doit s'allonger.
 *
 * Ne restent ici que les libellés qui disent « pas encore », et non « pas du
 * tout » : ceux-là ne sont pas des ennuis, seulement du transit.
 */
const MOTIFS_PAS_LIVRE = [
    /\ben\s+cours\s+de\s+livraison\b/,
    /\bout\s+for\s+delivery\b/,
    // Une TENTATIVE de livraison, sans dire qu'elle a échoué : le colis repasse.
    // « delivery attempt failed », lui, est un échec et vit dans la liste des
    // blocages — c'est le seul endroit où les deux listes se frôlent.
    /\bdelivery\s+attempt\b(?!\s+failed)/,
];
/**
 * Retire accents et casse pour comparer.
 *
 * Les transporteurs écrivent « Livré », « LIVRE » et « livre » pour la même
 * chose, et le même transporteur change de casse d'un événement à l'autre.
 * Comparer la forme aplatie évite d'avoir à prévoir chaque graphie.
 */
function aplatir(texte) {
    return texte
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Traduit un libellé transporteur en statut.
 *
 * L'ordre est celui de la gravité décroissante : un envoi bloqué reste bloqué
 * même si le libellé parle de livraison, et un « non livré » n'est jamais lu
 * comme livré.
 */
export function statutDepuisLibelle(libelle) {
    const texte = aplatir(String(libelle ?? ''));
    if (!texte)
        return 'inconnu';
    // Le blocage se teste sur le libellé D'ORIGINE : `MOTIFS_LIVRAISON_BLOQUEE`
    // est déjà insensible à la casse et vit dans le moteur de règles, seul
    // endroit où la liste doit s'allonger.
    if (estLivraisonBloquee(libelle))
        return 'bloque';
    if (MOTIFS_PAS_LIVRE.some((m) => m.test(texte)))
        return 'en_cours';
    if (MOTIFS_LIVRE.some((m) => m.test(texte)))
        return 'livre';
    return 'en_cours';
}
export function lireReleve(releve) {
    const statut = statutDepuisLibelle(releve.libelle);
    return {
        statut,
        libelle: releve.libelle.trim(),
        jour: releve.jour ?? null,
        livraisonPrevue: releve.livraisonPrevue ?? null,
        livre: statut === 'livre',
        bloque: statut === 'bloque',
    };
}
/**
 * Faut-il écrire ce relevé sur la commande ?
 *
 * Un relevé qui n'apprend rien ne doit pas toucher la commande : réécrire le
 * même libellé avec la date du jour ferait mentir « dernier point de suivi »,
 * et l'action « suivi sans mise à jour » ne se lèverait jamais — alors que
 * c'est précisément elle qui doit alerter quand le transporteur se tait.
 *
 * On écrit donc si, et seulement si, le libellé ou le jour a changé.
 */
export function releveApporteQuelqueChose(lecture, connu) {
    if (lecture.statut === 'inconnu')
        return false;
    if (aplatir(lecture.libelle) !== aplatir(connu.statutTransporteur ?? ''))
        return true;
    return lecture.jour !== null && lecture.jour !== connu.dateStatutTransporteur;
}
/**
 * Transporteurs pour lesquels un relevé automatique existe.
 *
 * Volontairement séparé de `TRANSPORTEURS_SUIVIS` — qui dit « on sait construire
 * un lien de suivi » — parce que les deux listes ne coïncident pas : FedEx a une
 * page de suivi ouvrable d'un clic, sans qu'on sache la relever faute de contrat
 * API.
 */
export const TRANSPORTEURS_RELEVABLES = [
    'Dachser', 'DSV', 'DHL', 'DPD', 'Rhenus',
];
/*
 * `estRelevable` a été retiré : aucun appelant.
 *
 * L'aiguillage (`RegistreSuivi`) ne demande pas si un transporteur est
 * relevable — il cherche un adaptateur, et n'en trouvant pas laisse le repli
 * parcelsapp tenter sa chance. Un transporteur hors de cette liste n'est donc
 * pas exclu du relevé, et la fonction répondait à une question que personne ne
 * posait. `TRANSPORTEURS_RELEVABLES` reste : elle sert à l'écran de santé.
 */
// ---------------------------------------------------------------------------
// Code postal : le second facteur des transporteurs
// ---------------------------------------------------------------------------
/**
 * Récupération du code postal du destinataire.
 *
 * Dachser et DPD en font un **second facteur** : sans lui, le numéro de suivi
 * seul n'ouvre rien. C'est délibéré de leur part — sinon un numéro deviné
 * donnerait accès à la livraison de n'importe qui.
 *
 * Or `adresse.codePostal` est vide sur une partie des commandes : celles qui
 * viennent de **Sellsy** portent l'adresse entière, en bloc, dans
 * `adresse.detailsLivraison`. Ne pas y chercher reviendrait à refuser de relever
 * une commande dont on a pourtant l'information sous les yeux.
 *
 * ## Pourquoi le pays compte
 *
 * Chercher « cinq chiffres » partout est faux pour la moitié de l'Europe : la
 * Belgique, les Pays-Bas, la Suisse, l'Autriche et le Luxembourg écrivent leurs
 * codes sur **quatre** chiffres, le Portugal sur `1234-567`, le Royaume-Uni et
 * l'Irlande en alphanumérique. Un motif unique manquerait les uns et se
 * tromperait sur les autres — un numéro de rue à quatre chiffres ressemble
 * beaucoup à un code postal belge.
 *
 * On lit donc le pays d'abord, et on applique SON format. Quand le pays est
 * inconnu, on essaie les formats du plus distinctif au plus ambigu, et on
 * exige qu'une ville suive : un code postal est suivi d'un nom de commune,
 * jamais d'une fin de ligne isolée.
 *
 * ## Se tromper est sans danger
 *
 * Un code postal erroné ne fait pas remonter le colis d'un autre : il ne fait
 * rien remonter du tout. Le transporteur s'en sert pour **confirmer** une
 * correspondance, jamais pour la trouver, et le numéro reste comparé à
 * l'identique de notre côté.
 */
/** Pays vers lesquels Mosaik expédie, avec leurs graphies rencontrées. */
const PAYS_CONNUS = [
    { code: 'FR', motifs: /^(fr|france|fra)$/i },
    { code: 'BE', motifs: /^(be|belgi(que|e|um)|bel)$/i },
    { code: 'LU', motifs: /^(lu|luxembourg|lux)$/i },
    { code: 'NL', motifs: /^(nl|pays[-\s]?bas|nederland|netherlands|holland)$/i },
    { code: 'DE', motifs: /^(de|allemagne|deutschland|germany|ger|deu)$/i },
    { code: 'AT', motifs: /^(at|autriche|[oö]sterreich|austria|aut)$/i },
    { code: 'CH', motifs: /^(ch|suisse|schweiz|switzerland|sui|che)$/i },
    { code: 'IT', motifs: /^(it|italie|italia|italy|ita)$/i },
    { code: 'ES', motifs: /^(es|espagne|espa[ñn]a|spain|esp)$/i },
    { code: 'PT', motifs: /^(pt|portugal|prt)$/i },
    { code: 'GB', motifs: /^(gb|uk|royaume[-\s]?uni|united\s?kingdom|angleterre|england|grande[-\s]?bretagne)$/i },
    { code: 'IE', motifs: /^(ie|irlande|ireland|eire|irl)$/i },
];
/**
 * Motif du code postal par pays.
 *
 * `motif` capture le code en premier groupe. Le suffixe `(?=\s*[-,\n]?\s*\p{L})`
 * exige qu'une lettre suive — la ville. C'est ce qui distingue « 1180 Uccle »
 * d'un numéro de commande à quatre chiffres perdu dans le même bloc.
 */
const FORMATS = {
    // Cinq chiffres. Assez distinctif pour se passer de la ville.
    FR: { motif: /(?<![\d-])(\d{5})(?![\d-])/gu, exigeVille: false },
    DE: { motif: /(?<![\d-])(\d{5})(?![\d-])/gu, exigeVille: false },
    IT: { motif: /(?<![\d-])(\d{5})(?![\d-])/gu, exigeVille: false },
    ES: { motif: /(?<![\d-])(\d{5})(?![\d-])/gu, exigeVille: false },
    // Quatre chiffres : trop proche d'un numéro de rue ou de commande pour être
    // pris seul. La ville qui suit est ce qui fait la différence.
    BE: { motif: /(?<![\d-])(\d{4})(?![\d-])/gu, exigeVille: true },
    LU: { motif: /(?<![\d-])(\d{4})(?![\d-])/gu, exigeVille: true },
    CH: { motif: /(?<![\d-])(\d{4})(?![\d-])/gu, exigeVille: true },
    AT: { motif: /(?<![\d-])(\d{4})(?![\d-])/gu, exigeVille: true },
    // Quatre chiffres suivis de deux lettres : « 1012 AB ». Sans ambiguïté.
    NL: { motif: /(?<![\d-])(\d{4}\s?[A-Z]{2})(?![\w])/gu, exigeVille: false },
    PT: { motif: /(?<![\d-])(\d{4}-\d{3})(?![\d-])/gu, exigeVille: false },
    GB: { motif: /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/giu, exigeVille: false },
    // Eircode irlandais : « A65 F4E2 ». Le premier caractère n'est jamais un
    // chiffre, ce qui le distingue d'un code britannique.
    IE: { motif: /\b([AC-FHKNPRTV-Y]\d{2}\s?[\dAC-FHKNPRTV-Y]{4})\b/giu, exigeVille: false },
};
/**
 * Ordre d'essai quand le pays est inconnu : du plus distinctif au plus ambigu.
 *
 * Les formats à quatre chiffres viennent en dernier et exigent une ville ; sans
 * cela, « 12 rue du Port 1000 Bruxelles » et « commande 4521 » seraient
 * indiscernables.
 */
const ORDRE_SANS_PAYS = ['NL', 'PT', 'IE', 'GB', 'FR', 'BE'];
/** Reconnaît le pays, depuis le champ dédié ou depuis le texte de l'adresse. */
export function paysDeLAdresse(pays, texte = '') {
    const brut = String(pays ?? '').trim();
    if (brut) {
        const trouve = PAYS_CONNUS.find((p) => p.motifs.test(brut));
        if (trouve)
            return trouve.code;
    }
    // Les blocs Sellsy finissent par le pays, sur sa propre ligne. On lit donc en
    // partant de la fin : une adresse peut contenir « France » ailleurs (dans un
    // nom d'entreprise), mais la dernière ligne est le pays.
    const lignes = String(texte).split(/[\n,]/).map((l) => l.trim()).filter(Boolean).reverse();
    for (const ligne of lignes) {
        const trouve = PAYS_CONNUS.find((p) => p.motifs.test(ligne));
        if (trouve)
            return trouve.code;
    }
    return null;
}
/** Cherche un code postal dans un texte, au format d'un pays donné. */
function chercher(texte, code) {
    const format = FORMATS[code];
    if (!format)
        return null;
    const candidats = [];
    // `matchAll` exige le drapeau `g`, que tous les motifs portent.
    for (const m of texte.matchAll(format.motif)) {
        const valeur = m[1];
        if (!valeur)
            continue;
        if (format.exigeVille) {
            // Une lettre doit suivre, éventuellement après une virgule ou un tiret.
            const apres = texte.slice((m.index ?? 0) + m[0].length);
            if (!/^\s*[-,]?\s*\p{L}/u.test(apres))
                continue;
        }
        candidats.push(valeur.toUpperCase().replace(/\s+/g, ' ').trim());
    }
    // Le dernier : une ligne d'adresse commence par un numéro de rue, qui précède
    // toujours le code postal.
    return candidats.length ? candidats[candidats.length - 1] : null;
}
/**
 * Code postal utilisable pour interroger un transporteur, `null` si introuvable.
 *
 * Le champ dédié d'abord — il est fiable quand il existe. À défaut, on lit
 * l'adresse au format du pays : `ligne` puis `detailsLivraison`, qui porte le
 * bloc entier sur les commandes Sellsy.
 */
export function codePostalDepuisAdresse(adresse) {
    const direct = String(adresse.codePostal ?? '').trim();
    if (direct)
        return direct;
    const sources = [adresse.ligne, adresse.ville, adresse.detailsLivraison]
        .map((t) => String(t ?? '').trim())
        .filter(Boolean);
    if (!sources.length)
        return null;
    const pays = paysDeLAdresse(adresse.pays, sources.join('\n'));
    for (const texte of sources) {
        if (pays) {
            const trouve = chercher(texte, pays);
            if (trouve)
                return trouve;
            // Pays connu et format absent : on n'essaie PAS les autres formats sur ce
            // texte. Une adresse belge sans code à quatre chiffres n'a pas un code
            // français caché dedans — ce serait un numéro de rue ou de commande.
            continue;
        }
        for (const candidat of ORDRE_SANS_PAYS) {
            const trouve = chercher(texte, candidat);
            if (trouve)
                return trouve;
        }
    }
    return null;
}
//# sourceMappingURL=livraison.js.map